import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { audienceMinerAccess } from "@/features/audience-miner/access/audience-miner-access";

type NavEntry = {
    to: string;
    label: string;
    icon: string;
};

type NavGroup = {
    label: string;
    items: NavEntry[];
};

const NAV_GROUPS: NavGroup[] = [
    {
        label: "Daily",
        items: [
            { to: "/command-center", label: "Command Center", icon: "monitor_heart" },
            { to: "/executive", label: "Executive", icon: "leaderboard" },
            { to: "/diagnostic", label: "Diagnostic", icon: "build" },
            { to: "/funnel-sla", label: "Funnel & SLA", icon: "filter_alt" },
        ],
    },
    {
        label: "Business",
        items: [
            { to: "/corridors", label: "Corridors & Clients", icon: "hub" },
            { to: "/revenue", label: "Revenue & Unit Economics", icon: "savings" },
            { to: "/customers", label: "Customers & Retention", icon: "groups" },
            { to: "/customer-intelligence", label: "Customer Intelligence", icon: "insights" },
            { to: "/meta-growth", label: "Meta Growth", icon: "campaign" },
        ],
    },
    {
        label: "Control",
        items: [
            { to: "/risk", label: "Risk, Treasury & Compliance", icon: "verified_user" },
            { to: "/investor", label: "Investor / Board", icon: "show_chart" },
            { to: "/experimental", label: "Experimental Lab", icon: "science" },
        ],
    },
    {
        label: "System",
        items: [
            { to: "/projector-health", label: "Projector Health", icon: "monitoring" },
            { to: "/shadow-parity", label: "Shadow Parity", icon: "difference" },
        ],
    },
];

const buildNavClass = ({ isActive }: { isActive: boolean }): string =>
    isActive ? "nav-item nav-item--active" : "nav-item";

const initialsOf = (name: string): string =>
    name
        .split(" ")
        .map((part) => part.charAt(0).toUpperCase())
        .filter((char) => char !== "")
        .slice(0, 2)
        .join("");

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

export const Sidebar: React.FC<Props> = ({ isOpen, onClose }) => {
    const { state, logout } = useAuth();
    const user = state.user;
    const [menuOpen, setMenuOpen] = useState(false);
    const footerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handleClick = (event: MouseEvent) => {
            if (footerRef.current === null) return;
            if (event.target instanceof Node && footerRef.current.contains(event.target)) return;
            setMenuOpen(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [menuOpen]);

    const displayName =
        user === null
            ? ""
            : user.name !== ""
              ? user.name
              : user.preferredUsername !== ""
                ? user.preferredUsername
                : user.email;

    const scrimClass = isOpen ? "sidebar__scrim is-open" : "sidebar__scrim";
    const sidebarClass = isOpen ? "sidebar is-open" : "sidebar";
    const navGroups = NAV_GROUPS.map((group) =>
        group.label === "Business" && audienceMinerAccess.can(state, "view")
            ? { ...group, items: [...group.items, { to: "/audience-miner/summary", label: "Audience Miner", icon: "travel_explore" }] }
            : group
    );

    return (
        <>
            <div className={scrimClass} onClick={onClose} aria-hidden="true" />
            <aside className={sidebarClass}>
                <div className="sidebar__brand">
                    <img
                        className="sidebar__brand-wordmark"
                        src={`${process.env.PUBLIC_URL ?? ""}/assets/metrics-lockup-horizontal-dark.svg`}
                        alt="Metrics by VirtuaBroker"
                    />
                </div>

                {navGroups.map((group) => (
                    <div key={group.label} className="sidebar__group">
                        <div className="sidebar__group-label">{group.label}</div>
                        {group.items.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                aria-label={item.label}
                                className={buildNavClass}
                                onClick={onClose}
                                end
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                ))}

                {user !== null ? (
                    <div className="sidebar__footer" ref={footerRef}>
                        <button
                            type="button"
                            className="sidebar__footer-trigger"
                            onClick={() => setMenuOpen((value) => !value)}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                        >
                            <span className="av">{initialsOf(displayName)}</span>
                            <span className="sidebar__footer-meta">
                                <span className="sidebar__footer-name">{displayName}</span>
                                {user.email !== "" && user.email !== displayName ? (
                                    <span className="sidebar__footer-email">{user.email}</span>
                                ) : null}
                            </span>
                            <span className="material-symbols-outlined sidebar__footer-chevron">
                                {menuOpen ? "expand_more" : "expand_less"}
                            </span>
                        </button>

                        {menuOpen ? (
                            <div className="sidebar__footer-menu" role="menu">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className="sidebar__footer-action"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        logout();
                                    }}
                                >
                                    <span className="material-symbols-outlined">logout</span>
                                    Log out
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </aside>
        </>
    );
};
